import { OverridableStringUnion } from "@mui/types";
import TooltipButton from "./TooltipButton";
import { Fragment } from "react/jsx-runtime";
import * as React from 'react';
import Button, { ButtonPropsColorOverrides } from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { AppBar, Toolbar, useMediaQuery } from "@mui/material";

export interface PrizaMenuOptions {
    tooltip: string,
    option: string,
    onClick: (option: string) => void,
    icon: React.ReactNode,
    color?: OverridableStringUnion<'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning', ButtonPropsColorOverrides>,
    disabled?: boolean,
}

export interface PrizaMenuProps {
    options: PrizaMenuOptions[]
    selected: string,
}

const PrizaMenu = ({ options, selected }: PrizaMenuProps) => {
    const width = options.length * 60 + 20;
    const isSmall = useMediaQuery(`(max-width:${width}px)`);
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = React.useState(1);

    if (!isSmall) {
        return (
            <AppBar key={"app-control"} id={"app-control"} position="static" color="default" elevation={2}>
                <Toolbar>
                    <ButtonGroup variant="contained" color="primary">
                        {options.map((option) => (
                            <TooltipButton
                                key={option.option}
                                option={option.option}
                                optionSelected={selected}
                                onClick={() => option.onClick(option.option)}
                                tooltip={option.tooltip}
                                disabled={option.disabled}
                                color={option.color || "primary"}
                            >
                                {option.icon}
                            </TooltipButton>
                        ))}
                    </ButtonGroup>
                </Toolbar>
            </AppBar>
        );
    } else {
        const handleToggle = () => {
            setOpen((prevOpen) => !prevOpen);
        };

        const handleClose = (event: Event) => {
            if (
                anchorRef.current &&
                anchorRef.current.contains(event.target as HTMLElement)
            ) {
                return;
            }

            setOpen(false);
        };

        const selectedOption = options.find(option => option.option === selected);
        const selectedIndexFromProps = options.findIndex(option => option.option === selected);

        return (
            <AppBar key={"app-control"} id={"app-control"} position="static" color="default" elevation={2}>
                <Toolbar>
                    <ButtonGroup
                        variant="contained"
                        ref={anchorRef}
                        aria-label="Opções"
                    >
                        <Button onClick={handleToggle}>{selectedOption?.tooltip}</Button>
                        <Button
                            size="small"
                            aria-controls={open ? 'split-button-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-label="Opções"
                            aria-haspopup="menu"
                            onClick={handleToggle}
                        >
                            <ArrowDropDownIcon />
                        </Button>
                    </ButtonGroup>
                    <Popper
                        sx={{ zIndex: 1 }}
                        open={open}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        transition
                        disablePortal
                    >
                        {({ TransitionProps, placement }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin:
                                        placement === 'bottom' ? 'center top' : 'center bottom',
                                }}
                            >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MenuList id="split-button-menu" autoFocusItem>
                                            {options.map((option, index) => (
                                                <MenuItem
                                                    key={`${option.option}-${index}`}
                                                    disabled={option.disabled}
                                                    selected={selected == option.option}
                                                    onClick={() => {
                                                        option.onClick(option.option);
                                                        setSelectedIndex(index);
                                                        setOpen(false);
                                                    }}
                                                >
                                                    {option.tooltip}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </Toolbar>
            </AppBar>
        );
    }
};

export default PrizaMenu;